import { FilterState } from '../types/brand'

// URL parameter keys for different filter types
const URL_PARAMS = {
  search: 's',
  amazonPresence: 'ap',
  manufacturingVerification: 'mv',
  priceTier: 'pt',
  productCategories: 'pc',
  foundedYearMin: 'fmin',
  foundedYearMax: 'fmax',
  confidenceMin: 'cmin',
  confidenceMax: 'cmax',
  states: 'st'
} as const

// Encode array values for URL (comma-separated)
function encodeArrayParam(arr: string[]): string {
  return arr.length > 0 ? arr.join(',') : ''
}

// Decode array values from URL
function decodeArrayParam(param: string | null): string[] {
  if (!param || param.trim() === '') return []
  return param.split(',').filter(item => item.trim() !== '')
}

// Encode filters to URL search parameters
export function encodeFiltersToURL(filters: FilterState): string {
  const params = new URLSearchParams()

  // Add search term
  if (filters.searchTerm.trim()) {
    params.set(URL_PARAMS.search, filters.searchTerm)
  }

  // Add array filters
  const amazonParam = encodeArrayParam(filters.amazonPresence)
  if (amazonParam) params.set(URL_PARAMS.amazonPresence, amazonParam)

  const manufacturingParam = encodeArrayParam(filters.manufacturingVerification)
  if (manufacturingParam) params.set(URL_PARAMS.manufacturingVerification, manufacturingParam)

  const priceTierParam = encodeArrayParam(filters.priceTier)
  if (priceTierParam) params.set(URL_PARAMS.priceTier, priceTierParam)

  const categoriesParam = encodeArrayParam(filters.productCategories)
  if (categoriesParam) params.set(URL_PARAMS.productCategories, categoriesParam)

  const statesParam = encodeArrayParam(filters.states)
  if (statesParam) params.set(URL_PARAMS.states, statesParam)

  // Add range filters (only if different from defaults)
  const [foundedMin, foundedMax] = filters.foundedYearRange
  if (foundedMin !== 1801) params.set(URL_PARAMS.foundedYearMin, foundedMin.toString())
  if (foundedMax !== 2025) params.set(URL_PARAMS.foundedYearMax, foundedMax.toString())

  const [confMin, confMax] = filters.confidenceScoreRange
  if (confMin !== 0) params.set(URL_PARAMS.confidenceMin, confMin.toString())
  if (confMax !== 10) params.set(URL_PARAMS.confidenceMax, confMax.toString())

  return params.toString()
}

// Decode filters from URL search parameters
export function decodeFiltersFromURL(searchParams: URLSearchParams, defaultFilters: FilterState): FilterState {
  const filters: FilterState = { ...defaultFilters }

  // Decode search term
  const searchTerm = searchParams.get(URL_PARAMS.search)
  if (searchTerm) {
    filters.searchTerm = searchTerm
  }

  // Decode array filters
  const amazonPresence = decodeArrayParam(searchParams.get(URL_PARAMS.amazonPresence))
  if (amazonPresence.length > 0) {
    filters.amazonPresence = amazonPresence as any[]
  }

  const manufacturingVerification = decodeArrayParam(searchParams.get(URL_PARAMS.manufacturingVerification))
  if (manufacturingVerification.length > 0) {
    filters.manufacturingVerification = manufacturingVerification as any[]
  }

  const priceTier = decodeArrayParam(searchParams.get(URL_PARAMS.priceTier))
  if (priceTier.length > 0) {
    filters.priceTier = priceTier as any[]
  }

  const productCategories = decodeArrayParam(searchParams.get(URL_PARAMS.productCategories))
  if (productCategories.length > 0) {
    filters.productCategories = productCategories
  }

  const states = decodeArrayParam(searchParams.get(URL_PARAMS.states))
  if (states.length > 0) {
    filters.states = states
  }

  // Decode range filters
  const foundedMinParam = searchParams.get(URL_PARAMS.foundedYearMin)
  const foundedMaxParam = searchParams.get(URL_PARAMS.foundedYearMax)
  if (foundedMinParam || foundedMaxParam) {
    const foundedMin = foundedMinParam ? parseInt(foundedMinParam, 10) : filters.foundedYearRange[0]
    const foundedMax = foundedMaxParam ? parseInt(foundedMaxParam, 10) : filters.foundedYearRange[1]
    filters.foundedYearRange = [foundedMin, foundedMax]
  }

  const confMinParam = searchParams.get(URL_PARAMS.confidenceMin)
  const confMaxParam = searchParams.get(URL_PARAMS.confidenceMax)
  if (confMinParam || confMaxParam) {
    const confMin = confMinParam ? parseInt(confMinParam, 10) : filters.confidenceScoreRange[0]
    const confMax = confMaxParam ? parseInt(confMaxParam, 10) : filters.confidenceScoreRange[1]
    filters.confidenceScoreRange = [confMin, confMax]
  }

  return filters
}

// Update browser URL without triggering navigation
export function updateURLWithFilters(filters: FilterState) {
  const encoded = encodeFiltersToURL(filters)
  const newURL = encoded ? `${window.location.pathname}?${encoded}` : window.location.pathname
  
  // Use replaceState to avoid adding to browser history for every filter change
  window.history.replaceState({}, '', newURL)
}

// Generate shareable URL for current filters
export function generateShareableURL(filters: FilterState): string {
  const encoded = encodeFiltersToURL(filters)
  const baseURL = `${window.location.protocol}//${window.location.host}${window.location.pathname}`
  return encoded ? `${baseURL}?${encoded}` : baseURL
}

// Check if current URL has any filter parameters
export function hasURLFilters(): boolean {
  return window.location.search.length > 0
}

// Clear all URL parameters
export function clearURLFilters() {
  window.history.replaceState({}, '', window.location.pathname)
}

// Validate and sanitize filters from URL (security measure)
export function validateFiltersFromURL(filters: FilterState): FilterState {
  const validatedFilters = { ...filters }

  // Validate search term length
  if (validatedFilters.searchTerm.length > 100) {
    validatedFilters.searchTerm = validatedFilters.searchTerm.substring(0, 100)
  }

  // Validate array lengths (prevent URL manipulation attacks)
  const maxArrayLength = 20
  
  if (validatedFilters.amazonPresence.length > maxArrayLength) {
    validatedFilters.amazonPresence = validatedFilters.amazonPresence.slice(0, maxArrayLength)
  }
  
  if (validatedFilters.manufacturingVerification.length > maxArrayLength) {
    validatedFilters.manufacturingVerification = validatedFilters.manufacturingVerification.slice(0, maxArrayLength)
  }
  
  if (validatedFilters.priceTier.length > maxArrayLength) {
    validatedFilters.priceTier = validatedFilters.priceTier.slice(0, maxArrayLength)
  }
  
  if (validatedFilters.productCategories.length > maxArrayLength) {
    validatedFilters.productCategories = validatedFilters.productCategories.slice(0, maxArrayLength)
  }
  
  if (validatedFilters.states.length > maxArrayLength) {
    validatedFilters.states = validatedFilters.states.slice(0, maxArrayLength)
  }

  // Validate year ranges
  const [foundedMin, foundedMax] = validatedFilters.foundedYearRange
  if (foundedMin < 1700 || foundedMin > 2030 || foundedMax < 1700 || foundedMax > 2030 || foundedMin > foundedMax) {
    validatedFilters.foundedYearRange = [1801, 2025] // Reset to defaults
  }

  // Validate confidence ranges
  const [confMin, confMax] = validatedFilters.confidenceScoreRange
  if (confMin < 0 || confMin > 10 || confMax < 0 || confMax > 10 || confMin > confMax) {
    validatedFilters.confidenceScoreRange = [0, 10] // Reset to defaults
  }

  return validatedFilters
}

// Create a summary of active filters for display
export function createFilterSummary(filters: FilterState): string[] {
  const summary: string[] = []

  if (filters.searchTerm.trim()) {
    summary.push(`Search: "${filters.searchTerm}"`)
  }

  if (filters.amazonPresence.length > 0) {
    summary.push(`Amazon: ${filters.amazonPresence.join(', ')}`)
  }

  if (filters.manufacturingVerification.length > 0) {
    summary.push(`Manufacturing: ${filters.manufacturingVerification.join(', ')}`)
  }

  if (filters.priceTier.length > 0) {
    summary.push(`Price: ${filters.priceTier.join(', ')}`)
  }

  if (filters.productCategories.length > 0) {
    const categories = filters.productCategories.length > 3 
      ? `${filters.productCategories.slice(0, 3).join(', ')}, +${filters.productCategories.length - 3} more`
      : filters.productCategories.join(', ')
    summary.push(`Categories: ${categories}`)
  }

  if (filters.states.length > 0) {
    const states = filters.states.length > 3
      ? `${filters.states.slice(0, 3).join(', ')}, +${filters.states.length - 3} more`
      : filters.states.join(', ')
    summary.push(`States: ${states}`)
  }

  const [foundedMin, foundedMax] = filters.foundedYearRange
  if (foundedMin !== 1801 || foundedMax !== 2025) {
    summary.push(`Founded: ${foundedMin}-${foundedMax}`)
  }

  const [confMin, confMax] = filters.confidenceScoreRange
  if (confMin !== 0 || confMax !== 10) {
    summary.push(`Confidence: ${confMin}-${confMax}`)
  }

  return summary
}